import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Loader2, CheckCircle2, Clock, MonitorSmartphone } from "lucide-react";
import {
  PAYMENT_DEFAULTS,
  getService,
  getChargeAmount,
  getRemainingAmount,
  getSessionReservation,
  getMode,
  formatMoney,
} from "@/payments/config";
import { initiatePayment, isPaymentConfigured } from "@/payments/paymentService";
import { RazorpayPaymentButton } from "@/payments/RazorpayPaymentButton";

/**
 * PayButton — an elegant "Reserve Your Session" card that updates AUTOMATICALLY
 * from the selected service. Every value and amount comes from
 * src/payments/config.js — the component holds no service-specific data.
 */
const DetailRow = ({ label, value, testid, accent = false }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-stone/60 last:border-0">
    <span className="kicker text-charcoal/45 text-[0.58rem]">{label}</span>
    <span
      data-testid={testid}
      className={`text-sm ${accent ? "font-serif text-forest text-lg" : "text-charcoal"}`}
    >
      {value}
    </span>
  </div>
);

export const PayButton = ({ serviceId, customer, className = "" }) => {
  const [loading, setLoading] = useState(false);

  const service = getService(serviceId);
  if (!service || service.paymentType === "enquiry") return null;

  const mode = getMode(service);
  const configured = isPaymentConfigured(serviceId);
  const isReservation = service.paymentType === "reservation";
  const chargeNow = getChargeAmount(service);
  const reservation = getSessionReservation(service);
  const remaining = getRemainingAmount(service);
  const modeLabel =
    service.mode === "Both" ? "Online / In-Person" : service.mode || "—";

  const supportingText = PAYMENT_DEFAULTS.reserveSupportingText.replace(
    "{reservation}",
    formatMoney(reservation)
  );

  const handleClick = async () => {
    setLoading(true);
    const result = await initiatePayment({ serviceId, customer });
    setLoading(false);
    if (result.status === "unconfigured") {
      toast("Reservations opening soon", {
        description: `Add the Razorpay Payment Link/Button for “${service.name}” in src/payments/config.js to enable reservations.`,
        duration: 5000,
      });
    } else if (result.status === "error") {
      toast("We couldn't open the payment window", {
        description: "Please try again in a moment.",
        duration: 5000,
      });
    }
  };

  const isHostedButton = mode === "payment_button" && configured;

  return (
    <div
      data-testid="pay-button-block"
      data-service={service.id}
      className={`border border-stone bg-ivory p-8 text-left ${className}`}
    >
      <p className="kicker text-gold mb-5 flex items-center gap-2">
        <ShieldCheck size={15} strokeWidth={1.6} /> {PAYMENT_DEFAULTS.reserveHeading}
      </p>

      <h3 className="font-serif font-light text-charcoal text-2xl leading-tight" data-testid="pay-service-name">
        {service.name}
      </h3>

      {/* Meta */}
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-charcoal/60 text-sm">
        <span className="inline-flex items-center gap-2" data-testid="pay-mode">
          <MonitorSmartphone size={14} strokeWidth={1.5} className="text-gold" /> {modeLabel}
        </span>
        <span className="inline-flex items-center gap-2" data-testid="pay-duration">
          <Clock size={14} strokeWidth={1.5} className="text-gold" /> {service.duration}
        </span>
      </div>

      {/* Breakdown */}
      <div className="mt-6">
        <DetailRow
          label="Total Energy Exchange"
          testid="pay-energy-exchange"
          value={`${formatMoney(service.energyExchange)}${service.unit ? ` · ${service.unit}` : ""}`}
          accent
        />
        {isReservation && (
          <>
            <DetailRow
              label="Session Reservation"
              testid="pay-session-reservation"
              value={formatMoney(reservation)}
            />
            <DetailRow
              label="Remaining Energy Exchange"
              testid="pay-remaining"
              value={formatMoney(remaining)}
            />
          </>
        )}
      </div>

      {isReservation && (
        <p className="mt-5 text-charcoal/60 text-sm leading-relaxed" data-testid="pay-supporting-text">
          {supportingText}
        </p>
      )}

      <div className="mt-7">
        {isHostedButton ? (
          <RazorpayPaymentButton buttonId={service.razorpay.paymentButtonId} />
        ) : (
          <button
            onClick={handleClick}
            disabled={loading}
            data-testid="pay-now-button"
            className="group inline-flex items-center gap-3 bg-forest text-ivory px-9 py-4 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-charcoal disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                {`${PAYMENT_DEFAULTS.reserveHeading} • ${formatMoney(chargeNow)}`}
                <ArrowRight size={16} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
              </>
            )}
          </button>
        )}
      </div>

      <p className="mt-5 flex items-start gap-2 text-forest/70 text-[0.72rem] leading-relaxed" data-testid="pay-appointment-note">
        <CheckCircle2 size={13} strokeWidth={1.6} className="mt-0.5 shrink-0" /> {PAYMENT_DEFAULTS.appointmentNote}
      </p>
      <p className="mt-2 flex items-center gap-2 text-charcoal/40 text-[0.7rem]" data-testid="pay-security-note">
        <ShieldCheck size={12} strokeWidth={1.6} /> {PAYMENT_DEFAULTS.securityNote}
      </p>
    </div>
  );
};

export default PayButton;
